/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { ContentSliderAssetFragment } from "@cosmos/hygraph-schema";

import {
	Animate,
	BackgroundContainer,
	CosmosTagProps,
	ResponsiveContainer,
	SectionContainer,
	SectionContainerProps,
	SectionHeading,
	Wrap,
} from "@components/elements";
import { cx } from "@panda/css";
import { Box, Flex, VStack } from "@panda/jsx";

import { ContentLargeImageMainStack } from "../content-large-image/ContentLargeImage.MainStack";
import { animateCustomCss, assetWrapStyles } from "../content-large-image/ContentLargeImage.section.styles";
import { ContentSliderAssetMedia } from "./ContentSliderAsset.Media";

export interface ContentSliderAssetSectionProps extends Omit<ContentSliderAssetFragment, "badges"> {
	className?: string;
	sectionIndex?: number;
	enableAnimate?: boolean;
	badges?: CosmosTagProps[] | null;
	date?: string | null;
	sectionContainerProps?: SectionContainerProps;
}

export const ContentSliderAssetSection = (props: ContentSliderAssetSectionProps | null): React.JSX.Element | null => {
	const {
		config,
		eyebrow,
		title,
		desc,
		buttons,
		badges,
		date,
		assets,
		flip,
		gradient,
		className,
		sectionIndex,
		enableAnimate: enableAnimateProp,
		sectionContainerProps,
	} = props || {};

	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(eyebrow || title || desc || buttons?.length || badges?.length || date || assets?.length);

	if (!validHeading && !validContent) return null;
	const enableAnimate = enableAnimateProp !== undefined ? enableAnimateProp : true;
	const isFirstSection = sectionIndex === 0;
	const mainStackTitleAs = isFirstSection
		? config?.heading?.title?.text
			? "h2"
			: "h1"
		: config?.heading?.title?.text
			? "h3"
			: "h2";

	return (
		<BackgroundContainer
			bgAnimation={config?.backgroundAnimation}
			bgImageAsset={config?.backgroundImage}
			bgFullHeight={config?.backgroundFullHeight}
			edsBackgroundColor={config?.edsBackgroundColor}
		>
			<ResponsiveContainer>
				<SectionContainer
					contentVisibility="unset"
					{...sectionContainerProps}
					disablePaddingBlockEnd={!!config?.disablePaddingBottom}
				>
					<VStack alignItems="stretch" gap="eds.dimension.56" className={cx("content-large-image-section", className)}>
						{validHeading ? (
							<SectionHeading
								{...config?.heading}
								title={{ ...config?.heading?.title, as: isFirstSection ? "h1" : "h2" }}
							/>
						) : null}
						{validContent ? (
							<Flex
								direction={{ base: flip ? "row-reverse" : "row", lgDown: "column" }}
								justifyContent="space-between"
								columnGap="eds.dimension.64"
								rowGap={{ md: "eds.dimension.80", mdDown: "eds.dimension.48" }}
								className={cx("section-flex")}
							>
								<ContentLargeImageMainStack
									animate={enableAnimate ? isFirstSection : false}
									eyebrow={eyebrow || ""}
									gradient={gradient}
									title={title}
									titleAs={mainStackTitleAs}
									desc={desc}
									buttons={buttons || []}
									badges={badges || []}
									date={date}
								/>
								{assets?.length ? (
									<Box className={cx("asset-wrap", assetWrapStyles)}>
										<Wrap
											condition={enableAnimate ? isFirstSection : false}
											wrap={(children) => (
												<Animate delay={0.5} type="slideLeft" customCss={animateCustomCss}>
													{children}
												</Animate>
											)}
										>
											<ContentSliderAssetMedia assets={assets} />
										</Wrap>
									</Box>
								) : null}
							</Flex>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
